import React, { useState, useMemo } from 'react';
import {
  Box,
  Alert,
  Stack,
  Paper,
  Chip,
  Grid,
  Typography,
} from '@mui/material';
import GlowButton from './shared/GlowButton';
import UtilityHeader from './shared/UtilityHeader';
import StyledTextArea from './shared/StyledTextArea';

const TextUtility: React.FC = () => {
  const [input, setInput] = useState(() => localStorage.getItem('text-input') || '');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');

  // Update localStorage when state changes
  React.useEffect(() => {
    localStorage.setItem('text-input', input);
  }, [input]);

  const textStats = useMemo(() => {
    const text = input.trim();
    return {
      characters: input.length,
      charactersNoSpaces: input.replace(/\s/g, '').length,
      words: text ? text.split(/\s+/).length : 0,
      lines: input.split('\n').length,
      paragraphs: text ? text.split(/\n\s*\n/).length : 0,
    };
  }, [input]);

  const toUpperCase = () => {
    setOutput(input.toUpperCase());
    setError('');
  };

  const toLowerCase = () => {
    setOutput(input.toLowerCase());
    setError('');
  };

  const toTitleCase = () => {
    const titleCase = input.replace(/\w\S*/g, (txt) =>
      txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
    setOutput(titleCase);
    setError('');
  };

  const toCamelCase = () => {
    const camelCase = input
      .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
        index === 0 ? word.toLowerCase() : word.toUpperCase()
      )
      .replace(/\s+/g, '');
    setOutput(camelCase);
    setError('');
  };

  const toSnakeCase = () => {
    const snakeCase = input
      .replace(/\W+/g, ' ')
      .split(/ |\B(?=[A-Z])/)
      .map(word => word.toLowerCase())
      .join('_');
    setOutput(snakeCase);
    setError('');
  };

  const reverseText = () => {
    setOutput(input.split('').reverse().join(''));
    setError('');
  };

  const removeExtraSpaces = () => {
    const cleaned = input.replace(/\s+/g, ' ').trim();
    setOutput(cleaned);
    setError('');
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setError('');
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(output);
    } catch (err) {
      setError('Failed to copy to clipboard');
    }
  };

  return (
    <Box>
      <UtilityHeader 
        title="Text Tool" 
        description="Transform, analyze, and manipulate your text with ease." 
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
          {error}
        </Alert>
      )}

      {/* Text Statistics */}
      <Paper sx={{ 
        p: 2.5, 
        mb: 4, 
        bgcolor: 'rgba(255,255,255,0.02)', 
        border: '1px solid', 
        borderColor: 'rgba(255,255,255,0.05)',
        borderRadius: 3 
      }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700, color: 'primary.main', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 2 }}>
          Statistics
        </Typography>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" gap={1}>
          <Chip label={`Characters: ${textStats.characters}`} variant="outlined" sx={{ borderRadius: 1.5 }} />
          <Chip label={`Words: ${textStats.words}`} variant="outlined" sx={{ borderRadius: 1.5 }} />
          <Chip label={`Lines: ${textStats.lines}`} variant="outlined" sx={{ borderRadius: 1.5 }} />
          <Chip label={`Paragraphs: ${textStats.paragraphs}`} variant="outlined" sx={{ borderRadius: 1.5 }} />
        </Stack>
      </Paper>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <StyledTextArea
            label="Input"
            rows={10}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Enter your text here..."
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <StyledTextArea
            label="Result"
            accentColor="secondary"
            rows={10}
            value={output}
            onChange={(e) => setOutput(e.target.value)}
            placeholder="Transformed text will appear here..."
          />
        </Grid>
      </Grid>

      <Stack direction="row" spacing={1.5} sx={{ mt: 4, flexWrap: 'wrap', gap: 1.5 }}>
        <GlowButton onClick={toUpperCase} disabled={!input.trim()}>UPPERCASE</GlowButton>
        <GlowButton onClick={toLowerCase} disabled={!input.trim()}>lowercase</GlowButton>
        <GlowButton onClick={toTitleCase} disabled={!input.trim()}>Title Case</GlowButton>
        <GlowButton variant="outlined" disableGlow onClick={toCamelCase} disabled={!input.trim()}>camelCase</GlowButton>
        <GlowButton variant="outlined" disableGlow onClick={toSnakeCase} disabled={!input.trim()}>snake_case</GlowButton>
        <GlowButton variant="outlined" disableGlow onClick={reverseText} disabled={!input.trim()}>Reverse</GlowButton>
        <GlowButton variant="outlined" disableGlow onClick={removeExtraSpaces} disabled={!input.trim()}>Clean Spaces</GlowButton>
        
        <Box sx={{ flexGrow: 1 }} />
        
        <GlowButton
          color="primary"
          onClick={copyToClipboard}
          disabled={!output}
          sx={{ px: 3 }}
        >
          Copy
        </GlowButton>
        <GlowButton
          variant="outlined"
          disableGlow
          color="inherit"
          onClick={clearAll}
          disabled={!input.trim() && !output.trim()}
          sx={{ opacity: 0.7 }}
        >
          Clear
        </GlowButton>
      </Stack>
    </Box>
  );
};

export default TextUtility;
